import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  GroupAdd as BulkIcon,
  Sync as SyncIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { usersAPI } from '../../services/api';
import type { UserManagement as UserType, ManagerOption } from '../../types';

const UserManagement: React.FC = () => {
  const { showSuccess, showError, showInfo } = useToast();

  // State
  const [users, setUsers] = useState<UserType[]>([]);
  const [managerOptions, setManagerOptions] = useState<ManagerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValue, setFilterValue] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkManagers, setBulkManagers] = useState<ManagerOption[]>([]);
  const [saving, setSaving] = useState<string | null>(null);

  // Fetch data
  useEffect(() => {
    fetchUsers();
    fetchManagers();
  }, [page, rowsPerPage, filterValue]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const filter = filterValue === 'all' ? undefined : filterValue;
      const response = await usersAPI.getAllUsers(
        searchTerm || undefined,
        filter,
        page + 1,
        rowsPerPage
      );
      setUsers(response.users);
      setTotalCount(response.totalCount);
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to load users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const managers = await usersAPI.getManagersList();
      setManagerOptions(managers);
    } catch (error: any) {
      console.error('Error fetching managers:', error);
    }
  };

  // Handlers
  const handleSearch = () => {
    setPage(0);
    fetchUsers();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSyncUsers = async () => {
    try {
      setSyncing(true);
      const result = await usersAPI.syncFromAzureAd();
      
      if (result.syncedCount > 0) {
        showSuccess(`Synced ${result.syncedCount} new user(s) from Azure AD`);
        await fetchUsers();
      } else {
        showInfo(result.message);
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to sync users from Azure AD');
      console.error('Error syncing users:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateManagers = async (userId: string, managerIds: string[]) => {
    try {
      setSaving(userId);
      await usersAPI.updateUserManagers(userId, managerIds);
      showSuccess('Managers updated successfully');
      await fetchUsers();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to update managers');
    } finally {
      setSaving(null);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.size === 0) {
      showError('Please select at least one user');
      return;
    }
    if (bulkManagers.length === 0) {
      showError('Please select at least one manager');
      return;
    }

    try {
      setSaving('bulk');
      const managerIds = bulkManagers.map(m => m.userId);
      await usersAPI.bulkUpdateManagers(Array.from(selectedUsers), managerIds);
      showSuccess(`Updated ${selectedUsers.size} user(s) successfully`);
      setSelectedUsers(new Set());
      setBulkDialogOpen(false);
      setBulkManagers([]);
      await fetchUsers();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to bulk update');
    } finally {
      setSaving(null);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(users.map(u => u.userId)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const getNoManagerCount = () => {
    return users.filter(u => u.managerIds.length === 0).length;
  };

  // Render
  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
            onClick={handleSyncUsers}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync from Azure AD'}
          </Button>
          {selectedUsers.size > 0 && (
            <Button
              variant="contained"
              startIcon={<BulkIcon />}
              onClick={() => setBulkDialogOpen(true)}
            >
              Assign Managers to {selectedUsers.size} Selected
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Users are automatically added to the database when they log in for the first time. 
          Currently showing {totalCount} user(s). Click "Sync from Azure AD" for status information.
        </Typography>
      </Alert>

      {/* Warning Alert */}
      {getNoManagerCount() > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            {getNoManagerCount()} user(s) without managers
          </Typography>
          <Typography variant="body2">
            Users without assigned managers cannot submit expense reports. Use the filters or bulk actions to assign managers.
          </Typography>
        </Alert>
      )}

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              sx={{ flexGrow: 1, minWidth: 250 }}
              label="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search users..."
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Search
            </Button>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filterValue}
                label="Filter"
                onChange={(e) => setFilterValue(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="no-manager">No Manager Assigned</MenuItem>
                <MenuItem value="has-manager">Has Manager</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedUsers.size === users.length && users.length > 0}
                    indeterminate={selectedUsers.size > 0 && selectedUsers.size < users.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Assigned Managers</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <UserRow
                  key={user.userId}
                  user={user}
                  managerOptions={managerOptions}
                  selected={selectedUsers.has(user.userId)}
                  saving={saving === user.userId}
                  onSelect={(checked) => handleSelectUser(user.userId, checked)}
                  onUpdate={(managerIds) => handleUpdateManagers(user.userId, managerIds)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </Card>

      {/* Bulk Update Dialog */}
      <Dialog 
        open={bulkDialogOpen} 
        onClose={() => !saving && setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Managers to {selectedUsers.size} User(s)
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
            Select one or more managers to assign to the selected users. This will replace any existing manager assignments.
          </Typography>
          <Autocomplete
            multiple
            options={managerOptions}
            getOptionLabel={(option) => `${option.displayName} (${option.email})`}
            value={bulkManagers}
            onChange={(e, newValue) => setBulkManagers(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Managers"
                placeholder="Choose managers..."
              />
            )}
            disabled={!!saving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={!!saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkUpdate}
            disabled={!!saving || bulkManagers.length === 0}
          >
            {saving === 'bulk' ? 'Updating...' : 'Assign Managers'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Individual User Row Component (unchanged)
interface UserRowProps {
  user: UserType;
  managerOptions: ManagerOption[];
  selected: boolean;
  saving: boolean;
  onSelect: (checked: boolean) => void;
  onUpdate: (managerIds: string[]) => void;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  managerOptions,
  selected,
  saving,
  onSelect,
  onUpdate,
}) => {
  const [editing, setEditing] = useState(false);
  const [selectedManagers, setSelectedManagers] = useState<ManagerOption[]>([]);

  useEffect(() => {
    // Initialize selected managers
    const managers = managerOptions.filter(m => user.managerIds.includes(m.userId));
    setSelectedManagers(managers);
  }, [user.managerIds, managerOptions]);

  const handleSave = () => {
    const managerIds = selectedManagers.map(m => m.userId);
    onUpdate(managerIds);
    setEditing(false);
  };

  const handleCancel = () => {
    const managers = managerOptions.filter(m => user.managerIds.includes(m.userId));
    setSelectedManagers(managers);
    setEditing(false);
  };

  return (
    <TableRow>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
        />
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight="medium">
            {user.displayName}
          </Typography>
          {user.managerIds.length === 0 && (
            <Chip
              label="No Manager"
              size="small"
              color="warning"
              icon={<WarningIcon />}
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {user.email}
        </Typography>
      </TableCell>
      <TableCell>
        {editing ? (
          <Autocomplete
            multiple
            size="small"
            options={managerOptions}
            getOptionLabel={(option) => `${option.displayName} (${option.email})`}
            value={selectedManagers}
            onChange={(e, newValue) => setSelectedManagers(newValue)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select managers..." />
            )}
            sx={{ minWidth: 300 }}
          />
        ) : (
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {user.managerIds.length === 0 ? (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                None assigned
              </Typography>
            ) : (
              selectedManagers.map((manager) => (
                <Chip
                  key={manager.userId}
                  label={manager.displayName}
                  size="small"
                  variant="outlined"
                />
              ))
            )}
          </Box>
        )}
      </TableCell>
      <TableCell align="center">
        {editing ? (
          <Box display="flex" gap={1} justifyContent="center">
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default UserManagement;